// Mirror of rfq/backend/costing.py so the station editor can show a live cost
// breakdown before saving. Keep the rules in sync with the backend:
//   - line goods = is_free_issued ? 0 : quantity * equipment.unit_cost
//   - lines carry no labor
//   - station total labor = sum over setup_labor of hours * rate
//   - missing rate -> 0 (hours preserved)

import {
    ConveyorStationConfig,
    ConveyorStationWithCost,
    Equipment,
    LaborRate,
    StationEquipmentLine,
    StationEquipmentLineCost,
    StationSetupLaborCost,
    StationSetupLaborEntry,
} from '../types';

interface Catalog {
    equipmentById: Map<string, Equipment>;
    ratesByRole: Map<string, LaborRate>;
}

function buildCatalog(equipment: Equipment[], rates: LaborRate[]): Catalog {
    return {
        equipmentById: new Map(equipment.map((e) => [e.id, e])),
        ratesByRole: new Map(rates.map((r) => [r.role, r])),
    };
}

export function computeLineCost(
    line: StationEquipmentLine,
    equipment: Equipment[],
    rates: LaborRate[]
): StationEquipmentLineCost | null {
    return computeLineWithCatalog(line, buildCatalog(equipment, rates));
}

function computeLineWithCatalog(
    line: StationEquipmentLine,
    catalog: Catalog
): StationEquipmentLineCost | null {
    const item = catalog.equipmentById.get(line.equipment_id);
    if (!item) return null;

    const isFree = line.is_free_issued ?? false;
    const goods = isFree ? 0 : line.quantity * item.unit_cost;
    return {
        equipment_id: item.id,
        equipment_name: item.name,
        quantity: line.quantity,
        is_free_issued: isFree,
        unit_cost: item.unit_cost,
        goods,
        total: goods,
        from_process_template_id: line.from_process_template_id ?? null,
    };
}

function computeSetupLaborWithCatalog(
    entry: StationSetupLaborEntry,
    catalog: Catalog
): StationSetupLaborCost {
    const roleRates = catalog.ratesByRole.get(entry.role);
    const rate = !roleRates
        ? 0
        : entry.worker_type === 'external'
            ? roleRates.external_rate
            : roleRates.internal_rate;
    return {
        role: entry.role,
        role_name: roleRates?.name ?? entry.role,
        hours: entry.hours,
        rate,
        amount: entry.hours * rate,
        worker_type: entry.worker_type,
        from_process_template_id: entry.from_process_template_id ?? null,
    };
}

export function computeSetupLaborCost(
    entry: StationSetupLaborEntry,
    rates: LaborRate[]
): StationSetupLaborCost {
    return computeSetupLaborWithCatalog(entry, buildCatalog([], rates));
}

export function computeStationCosts(
    stations: ConveyorStationConfig[],
    equipment: Equipment[],
    rates: LaborRate[]
): ConveyorStationWithCost[] {
    const catalog = buildCatalog(equipment, rates);
    return stations.map((station) => {
        const lines = (station.equipment ?? [])
            .map((line) => computeLineWithCatalog(line, catalog))
            .filter((l): l is StationEquipmentLineCost => l !== null);
        const setupLabor = (station.setup_labor ?? []).map((entry) =>
            computeSetupLaborWithCatalog(entry, catalog)
        );
        const totalGoods = lines.reduce((acc, l) => acc + l.goods, 0);
        const totalLabor = setupLabor.reduce((acc, l) => acc + l.amount, 0);
        return {
            station_type_id: station.station_type_id,
            name: station.name,
            comments: station.comments,
            steps: station.steps ?? [],
            equipment: lines,
            setup_labor: setupLabor,
            total_goods: totalGoods,
            total_labor: totalLabor,
            total: totalGoods + totalLabor,
        };
    });
}

export function formatCurrency(n: number): string {
    return n.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    });
}
